/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useFormPermissions.ts
 🗓️ Created: 2025-01-06
 🧩 Description:
    Custom hook for managing form-level permissions and UI controls.
    Provides utilities for showing/hiding form elements based on user permissions.

 🔧 Notes:
    - Integrates with usePermissions for permission checking
    - Provides form-specific permission utilities
    - Supports field-level and action-level permissions
    - Implements UI state management based on permissions

 🔁 Last Updated: 2025-01-06 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { usePermissions, MODULE_SLUGS } from './usePermissions';

export const useFormPermissions = (moduleId: string | number) => {
  const { 
    hasModulePermission, 
    canWriteModule, 
    hasFullModuleAccess,
    getModulePermissionLevel 
  } = usePermissions();

  const permissionLevel = getModulePermissionLevel(moduleId);

  // Form-level permissions
  const canView = hasModulePermission(moduleId, 'READ');
  const canEdit = canWriteModule(moduleId);
  const canDelete = hasFullModuleAccess(moduleId);
  const canCreate = canWriteModule(moduleId);

  // Field-level permissions (can be customized per form)
  const getFieldPermission = (fieldType: 'readonly' | 'editable' | 'sensitive' = 'editable') => {
    switch (fieldType) {
      case 'readonly':
        return canView;
      case 'editable':
        return canEdit;
      case 'sensitive':
        return canDelete; // Sensitive fields require full access
      default:
        return canEdit;
    }
  };

  // Action-level permissions
  const getActionPermission = (action: 'create' | 'update' | 'delete' | 'duplicate' | 'export') => {
    switch (action) {
      case 'create':
        return canCreate;
      case 'update':
        return canEdit;
      case 'delete':
        return canDelete;
      case 'duplicate':
        return canEdit;
      case 'export':
        return canView;
      default:
        return false;
    }
  };

  // UI state helpers
  const getButtonProps = (action: 'create' | 'update' | 'delete' | 'duplicate' | 'export') => {
    const hasPermission = getActionPermission(action);
    
    return {
      disabled: !hasPermission,
      className: hasPermission ? '' : 'opacity-50 cursor-not-allowed',
      title: hasPermission ? '' : `Requires ${action === 'delete' ? 'FULL' : 'WRITE'} access`
    };
  };

  const getFieldProps = (fieldType: 'readonly' | 'editable' | 'sensitive' = 'editable') => {
    const hasPermission = getFieldPermission(fieldType);
    
    return {
      disabled: !hasPermission,
      className: hasPermission ? '' : 'opacity-50 cursor-not-allowed',
      title: hasPermission ? '' : `Requires ${fieldType === 'sensitive' ? 'FULL' : 'WRITE'} access`
    };
  };

  // Form mode helpers
  const getFormMode = () => {
    if (!canView) return 'hidden';
    if (!canEdit) return 'readonly';
    return 'editable';
  };

  const isFormReadonly = () => !canEdit;
  const isFormHidden = () => !canView;

  // Permission messages
  const getPermissionMessage = (action: string) => {
    const messages = {
      create: `You need WRITE access to create new ${moduleId}`,
      update: `You need WRITE access to update ${moduleId}`,
      delete: `You need FULL access to delete ${moduleId}`,
      duplicate: `You need WRITE access to duplicate ${moduleId}`,
      export: `You need READ access to export ${moduleId} data`
    };
    
    return messages[action as keyof typeof messages] || 'Insufficient permissions';
  };

  return {
    // Basic permissions
    canView,
    canEdit,
    canDelete,
    canCreate,
    permissionLevel,
    
    // Field permissions
    getFieldPermission,
    getFieldProps,
    
    // Action permissions
    getActionPermission,
    getButtonProps,
    
    // Form state
    getFormMode,
    isFormReadonly,
    isFormHidden,
    
    // Messages
    getPermissionMessage
  };
};
